import { Link } from 'react-router-dom'
import { ArrowRight, Hammer, TrendingUp, ListChecks, Phone } from 'lucide-react'
import Section from '../components/layout/Section'
import Reveal from '../components/layout/Reveal'
import Seo from '../components/seo/Seo'
import { useBreadcrumbs } from '../seo/useBreadcrumbs'
import { serviceSchema, faqSchema } from '../seo/schema'
import { AREAS } from '../data/areas'

const SELLER_FAQ = [
  {
    question: 'What is the realty transfer tax when selling in Delaware?',
    answer:
      'Delaware\'s realty transfer tax is 4% of the sale price and is customarily split equally between buyer and seller — so sellers typically pay 2%. The rate and any negotiated split should be confirmed at contract time, as local practice can vary and rates can change.',
  },
  {
    question: 'When is the best time to sell a beach house at the Delaware beaches?',
    answer:
      'Listing activity and buyer traffic typically peak from early spring through summer, when second-home buyers are in town and properties show best. That said, serious buyers shop year-round here, and low-inventory stretches in the off-season can favor sellers. The right timing depends on your property and goals — we\'ll show you actual seasonal sales patterns for your town.',
  },
  {
    question: 'Should I renovate before listing my Delaware beach home?',
    answer:
      'Not always — but targeted preparation almost always pays. Shawn\'s background as a builder and historic-home restorer means we can walk your property and tell you which fixes return their cost (and which don\'t), what buyers and inspectors will flag, and what to leave alone. Sometimes the right answer is pricing for condition instead of renovating.',
  },
  {
    question: 'How do you price a home in a resort market?',
    answer:
      'Resort pricing is hyper-local: ocean-block vs. off-beach, rental history, flood zone, and community amenities all move value far more than square footage alone. We price from actual comparable sales in your specific community — not county averages — and we\'ll show you the comps and the reasoning behind every number.',
  },
  {
    question: 'Do I need to disclose flood history when selling at the beach?',
    answer:
      'Delaware law requires sellers to complete a property disclosure, and flood-related history and known conditions must be disclosed honestly. Buyers at the beach will pull flood zones and insurance quotes regardless — straightforward disclosure up front protects you and keeps deals together.',
  },
]

const STEPS = [
  {
    title: 'Walk-Through & Straight Talk',
    body: 'Shawn walks your property with a builder\'s eye shaped by hands-on construction and historic restoration work — and tells you honestly what\'s worth fixing, what buyers will flag, and what to leave alone.',
  },
  {
    title: 'Pricing From Real Comps',
    body: 'We price from actual sales in your community and street — not county averages — factoring in rental history, flood zone, and the walk-to-beach premium that defines Delaware beach values.',
  },
  {
    title: 'Preparation & Presentation',
    body: 'Professional photography, staging guidance where it pays, and listing copy written by people who actually know your town — not boilerplate. Pre-listing prep is where our construction background saves sellers real money.',
  },
  {
    title: 'Launch Through Jack Lingo & Bright MLS',
    body: 'Your listing launches across the Bright MLS network and the region\'s largest independent brokerage — Jack Lingo REALTOR®, trusted at the Delaware beaches since 1974 — reaching the DC, Baltimore, and Philadelphia buyer pools.',
  },
  {
    title: 'Negotiation Through Closing',
    body: 'We manage offers, inspections, appraisal, and the Delaware transfer-tax split with clear communication at every step — from ratified contract to keys handed over.',
  },
]

export default function Sell() {
  const crumbs = useBreadcrumbs([{ label: 'Sell', path: '/sell' }])
  return (
    <div className="bg-neutral-charcoal">
      <Seo
        title="Sell Your Delaware Beach Home | Home Valuation — Lewes & Rehoboth"
        description="Selling a home in Lewes, Rehoboth, Dewey, Bethany, Fenwick or Milton? Builder's-eye prep, comp-based pricing, and Jack Lingo reach. Free valuation from Shawn & Merritt."
        path="/sell"
        jsonLd={[
          serviceSchema(
            'Real estate listing agent',
            'Listing and seller representation for homes and condos across the Delaware beaches — Lewes, Rehoboth Beach, Dewey Beach, Bethany Beach, Fenwick Island, and Milton.',
          ),
          crumbs.jsonLd,
          faqSchema(SELLER_FAQ),
        ]}
      />

      {/* Hero */}
      <Section className="bg-neutral-charcoal pt-28 pb-16">
        {crumbs.node}
        <Reveal>
          <p className="font-display text-[12px] uppercase tracking-[0.3em] font-medium text-white/70">
            For Sellers
            <span className="block w-6 h-[2px] bg-accent-gold mt-3" aria-hidden="true" />
          </p>
          <h1 className="mt-4 font-display uppercase text-white leading-none text-[clamp(36px,5.5vw,72px)]">
            <span className="font-thin">Sell Your </span>
            <span className="font-bold">Delaware Beach Home</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-white/70 font-light leading-[1.9]">
            Selling at the beach is different — lot value, flood zones, rental
            history, and walkability move prices as much as the house itself.
            You deserve agents who can read all of it, and a brokerage the
            buyers already trust.
          </p>
        </Reveal>
      </Section>

      {/* Why us */}
      <Section className="bg-primary-ink">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <Reveal>
            <Hammer className="h-8 w-8 text-accent-gold mb-4" aria-hidden="true" />
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white mb-3">
              A Builder&apos;s Eye on Prep
            </h2>
            <p className="text-white/70 font-light leading-[1.9]">
              Shawn is an Associate Broker with hands-on construction and
              historic-restoration experience. Before you spend a dollar on
              pre-listing work, he&apos;ll tell you which repairs return their
              cost, which inspectors will flag, and which projects to skip
              entirely.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <TrendingUp className="h-8 w-8 text-accent-gold mb-4" aria-hidden="true" />
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white mb-3">
              Pricing a Resort Market
            </h2>
            <p className="text-white/70 font-light leading-[1.9]">
              Beach values are hyper-local — two streets apart can mean a
              six-figure difference. We price from actual comps in your
              community, show you the reasoning, and position for the buyer
              pool your property actually attracts.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <ListChecks className="h-8 w-8 text-accent-gold mb-4" aria-hidden="true" />
            <h2 className="font-display text-lg font-bold uppercase tracking-wide text-white mb-3">
              The Jack Lingo Reach
            </h2>
            <p className="text-white/70 font-light leading-[1.9]">
              Your listing rides the region&apos;s largest independent brokerage
              and the full Bright MLS network — syndicated to the portals the
              DC, Baltimore, and Philadelphia second-home buyers actually
              search.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Process */}
      <Section className="bg-neutral-charcoal">
        <Reveal>
          <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-12">
            <span className="font-thin">How We </span>
            <span className="font-bold">Sell</span>
          </h2>
        </Reveal>
        <ol className="max-w-3xl space-y-8">
          {STEPS.map((step, i) => (
            <Reveal key={step.title} delay={i * 60}>
              <li className="flex gap-6">
                <span className="font-display text-4xl font-bold text-accent-gold/40 leading-none shrink-0 w-12">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-wide text-white">
                    {step.title}
                  </h3>
                  <p className="mt-2 text-white/70 font-light leading-[1.9]">{step.body}</p>
                </div>
              </li>
            </Reveal>
          ))}
        </ol>
      </Section>

      {/* FAQ */}
      <Section className="bg-primary-ink">
        <div className="max-w-3xl">
          <Reveal>
            <h2 className="font-display uppercase text-white leading-none text-[clamp(28px,4vw,48px)] mb-8">
              <span className="font-thin">Seller </span>
              <span className="font-bold">FAQs</span>
            </h2>
          </Reveal>
          <div className="space-y-3">
            {SELLER_FAQ.map((f) => (
              <details
                key={f.question}
                className="group border border-white/10 bg-white/5 open:border-accent-gold/40"
              >
                <summary className="cursor-pointer list-none p-5 font-display text-sm font-bold uppercase tracking-wide text-white flex items-center justify-between gap-4 [&::-webkit-details-marker]:hidden">
                  {f.question}
                  <span className="text-accent-gold text-xl leading-none transition-transform group-open:rotate-45" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p className="px-5 pb-5 text-white/70 font-light leading-[1.9]">{f.answer}</p>
              </details>
            ))}
          </div>
          <p className="mt-8 text-white/60 font-light">
            More questions? Our <Link to="/faq" className="text-accent-gold hover:underline">Delaware beach real estate FAQ</Link> covers
            taxes, rentals, and the buying side — or just <Link to="/contact" className="text-accent-gold hover:underline">ask us directly</Link>.
          </p>
        </div>
      </Section>

      {/* Town links */}
      <Section className="bg-neutral-charcoal">
        <Reveal>
          <h2 className="font-display uppercase text-white leading-none text-[clamp(24px,3vw,36px)] mb-8">
            <span className="font-thin">Selling in a </span>
            <span className="font-bold">Specific Town?</span>
          </h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {AREAS.map((a) => (
            <Link
              key={a.slug}
              to={`/areas/${a.slug}`}
              className="border border-white/10 bg-white/5 p-5 font-display text-sm font-bold uppercase tracking-wide text-white hover:border-accent-gold/50 transition-colors flex items-center justify-between gap-2"
            >
              {a.name}
              <ArrowRight className="h-4 w-4 text-accent-gold" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section className="bg-accent-gold py-16">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="font-display uppercase text-primary-ink leading-none text-[clamp(26px,3.5vw,44px)]">
              <span className="font-thin">What&apos;s your home </span>
              <span className="font-bold">worth?</span>
            </h2>
            <p className="mt-3 text-primary-ink/80 max-w-xl">
              A real valuation from agents who know your street — not an
              algorithm. Free, honest, and no obligation.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 shrink-0">
            <Link
              to="/contact?subject=VALUATION"
              className="inline-flex items-center justify-center gap-2 bg-primary-ink text-white px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-neutral-charcoal transition-colors"
            >
              Request a Valuation <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a
              href="tel:+13025428591"
              className="inline-flex items-center justify-center gap-2 border-2 border-primary-ink text-primary-ink px-8 py-4 text-sm font-display font-bold uppercase tracking-[0.15em] hover:bg-primary-ink hover:text-white transition-colors"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> 302-542-8591
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
